/* =====================================================================================================
   accountingApi.ts - the only place the Accounting screen talks to the backend.
   Everything goes through the gateway (default https://acc.cilpron.com) with the user's JWT.
   Change BASE_URL / token lookup below, or call configureAccountingApi() once at start-up.
   ===================================================================================================== */

export type Val = string | number;
export interface Row {
  id: string;
  [k: string]: Val;
}
export type Data = Record<string, Row[]>;

/* ---------- configuration ---------- */
export interface ApiConfig {
  baseUrl: string;
  getToken: () => string | null;
  onUnauthorized?: () => void;
}

const TOKEN_KEYS = ["access_token", "token", "jwt", "authToken", "cilpron_token"];
const readToken = (): string | null => {
  for (const store of [() => window.localStorage, () => window.sessionStorage]) {
    try {
      const s = store();
      for (const k of TOKEN_KEYS) {
        const v = s.getItem(k);
        if (v) return v.replace(/^"|"$/g, "").replace(/^Bearer\s+/i, "");
      }
    } catch {
      /* storage blocked */
    }
  }
  return null;
};

let CFG: ApiConfig = { baseUrl: "https://acc.cilpron.com", getToken: readToken };
export const configureAccountingApi = (c: Partial<ApiConfig>) => {
  CFG = { ...CFG, ...c };
};

export class ApiError extends Error {
  status: number;
  constructor(message: string, status: number) {
    super(message);
    this.status = status;
  }
}

type Q = Record<string, string | number | boolean | undefined | null>;
const qs = (q?: Q) => {
  if (!q) return "";
  const p = Object.entries(q)
    .filter(([, v]) => v !== undefined && v !== null && v !== "")
    .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(String(v))}`);
  return p.length ? `?${p.join("&")}` : "";
};

async function http<T = any>(method: string, path: string, body?: unknown, q?: Q): Promise<T> {
  const headers: Record<string, string> = { Accept: "application/json" };
  const token = CFG.getToken();
  if (token) headers.Authorization = `Bearer ${token}`;
  if (body !== undefined) headers["Content-Type"] = "application/json";
  let res: Response;
  try {
    res = await fetch(`${CFG.baseUrl.replace(/\/$/, "")}${path}${qs(q)}`, { method, headers, body: body === undefined ? undefined : JSON.stringify(body) });
  } catch {
    throw new ApiError("Can't reach the accounting service. Check your connection.", 0);
  }
  if (res.status === 401) CFG.onUnauthorized?.();
  if (res.status === 204) return undefined as T;
  const text = await res.text();
  let json: any = null;
  try {
    json = text ? JSON.parse(text) : null;
  } catch {
    json = null;
  }
  if (!res.ok) {
    let msg = json?.detail || json?.title || json?.message || (typeof json === "string" ? json : "");
    if (json?.errors && typeof json.errors === "object") {
      const first = Object.values(json.errors as Record<string, string[]>)[0];
      if (Array.isArray(first) && first[0]) msg = first[0];
    }
    if (!msg) msg = res.status === 401 ? "You are signed out. Sign in again." : res.status === 403 ? "You don't have permission to do that." : `Request failed (${res.status}).`;
    throw new ApiError(String(msg), res.status);
  }
  return json as T;
}
const GET = <T = any>(path: string, q?: Q) => http<T>("GET", path, undefined, q);
const POST = <T = any>(path: string, body?: unknown) => http<T>("POST", path, body ?? {});
const PUT = <T = any>(path: string, body?: unknown) => http<T>("PUT", path, body ?? {});
const DEL = <T = any>(path: string) => http<T>("DELETE", path);

/* ---------- small helpers ---------- */
const items = (p: any): any[] => (Array.isArray(p) ? p : Array.isArray(p?.items) ? p.items : []);
const ds = (v: any): string => (v ? String(v).slice(0, 10) : "");
const n = (v: any): number => (typeof v === "number" ? v : Number(v) || 0);
const s = (v: any): string => (v === null || v === undefined ? "" : String(v));
const pad = (k: number, w = 4) => String(k).padStart(w, "0");
const rows = (d: Data, k: string): Row[] => d[k] ?? [];
export const today = () => {
  const t = new Date();
  return `${t.getFullYear()}-${pad(t.getMonth() + 1, 2)}-${pad(t.getDate(), 2)}`;
};
const PAGE = { page: 1, pageSize: 500 };

/** numeric database id behind a row (by its display id) */
export const dbId = (d: Data, key: string, id: Val | undefined): number => n(rows(d, key).find((r) => r.id === String(id))?._id);
const raw: Record<string, Record<string, any>> = {};
const keep = (k: string, id: string, o: any) => {
  (raw[k] ??= {})[id] = o;
};
const kept = (k: string, id: string): any => raw[k]?.[id] ?? {};

/* ---------- status vocabulary: backend <-> screen ---------- */
const docStatus = (st: string, open: "Sent" | "Approved"): string =>
  ({ Draft: "Draft", Unpaid: open, PartPaid: "Partially paid", Paid: "Paid", Disputed: "Disputed", Cancelled: "Void" } as Record<string, string>)[st] ?? st;
const methodOut = (m: string) => (m === "Bank transfer" ? "Bank Transfer" : m);

/* =====================================================================================================
   Loading
   ===================================================================================================== */
export interface LoadResult {
  data: Data;
  errors: string[];
}

type Loader = (d: Data) => Promise<Row[]>;

const L: Record<string, { needs: string[]; run: Loader }> = {
  accounts: {
    needs: [],
    run: async () =>
      (await GET<any[]>("/api/dashboard/ledger/accounts")).map((a) => {
        keep("accounts", s(a.code), a);
        return { id: s(a.code), _id: n(a.accountId), name: s(a.name), type: s(a.type), group: s(a.group), groupId: n(a.groupId), balance: n(a.balance), isBank: a.isBank ? 1 : 0, status: a.isActive ? "Active" : "Inactive" } as Row;
      }),
  },
  groups: {
    needs: [],
    run: async () => (await GET<any[]>("/api/dashboard/ledger/groups")).map((g) => ({ id: s(g.groupId), _id: n(g.groupId), name: s(g.name), type: s(g.type), label: `${s(g.type)} · ${s(g.name)}` }) as Row),
  },
  customers: {
    needs: [],
    run: async () => {
      const [list, bal] = await Promise.all([GET("/api/receivables/customers", { ...PAGE, includeInactive: true }), GET<any[]>("/api/dashboard/lookups/customers").catch(() => [] as any[])]);
      const owes = new Map<number, number>(bal.map((b: any) => [n(b.partyId), n(b.outstanding)]));
      return items(list).map((c) => {
        keep("customers", s(c.customerCode), c);
        return { id: s(c.customerCode), _id: n(c.customerId), name: s(c.customerName), email: s(c.email), phone: s(c.phone), terms: s(c.paymentTerms), owes: owes.get(n(c.customerId)) ?? 0, status: c.isActive ? "Active" : "Inactive" } as Row;
      });
    },
  },
  vendors: {
    needs: [],
    run: async () => {
      const [list, bal] = await Promise.all([GET("/api/payables/vendors", { ...PAGE, includeInactive: true }), GET<any[]>("/api/dashboard/lookups/vendors").catch(() => [] as any[])]);
      const owed = new Map<number, number>(bal.map((b: any) => [n(b.partyId), n(b.outstanding)]));
      return items(list).map((c) => {
        keep("vendors", s(c.vendorCode), c);
        return { id: s(c.vendorCode), _id: n(c.vendorId), name: s(c.vendorName), email: s(c.email), phone: s(c.phone), terms: s(c.paymentTerms), owed: owed.get(n(c.vendorId)) ?? 0, status: c.isActive ? "Active" : "Inactive" } as Row;
      });
    },
  },
  taxes: {
    needs: [],
    run: async () =>
      items(await GET("/api/tax/codes", PAGE)).map((t) => {
        keep("taxes", s(t.code), t);
        return { id: s(t.code), _id: n(t.taxCodeId), name: s(t.description), type: s(t.taxType), rate: n(t.rate), status: t.isActive ? "Active" : "Inactive" } as Row;
      }),
  },
  banks: {
    needs: [],
    run: async () =>
      (await GET<any[]>("/api/dashboard/lookups/banks")).map((b) => ({ id: s(b.bankAccountId), _id: n(b.bankAccountId), name: s(b.name), bank: s(b.bank), number: s(b.accountNumber), gl: s(b.ledgerAccountCode), glId: n(b.ledgerAccountId), balance: n(b.balance) }) as Row),
  },
  invoices: {
    needs: ["customers"],
    run: async (d) => {
      const code = new Map(rows(d, "customers").map((c) => [n(c._id), c.id]));
      return items(await GET("/api/receivables/invoices", PAGE)).map((i) => ({
        id: s(i.invoiceNumber), _id: n(i.invoiceId), customer: code.get(n(i.customerId)) ?? s(i.customerName), date: ds(i.invoiceDate), due: ds(i.dueDate),
        total: n(i.netAmount), paid: n(i.netAmount) - n(i.balanceDue), status: docStatus(s(i.status), "Sent"),
      }) as Row);
    },
  },
  payables: {
    needs: ["vendors"],
    run: async (d) => {
      const code = new Map(rows(d, "vendors").map((c) => [n(c._id), c.id]));
      return items(await GET("/api/payables/invoices", PAGE)).map((i) => ({
        id: s(i.invoiceNumber), _id: n(i.invoiceId), vendor: code.get(n(i.vendorId)) ?? s(i.vendorName), date: ds(i.invoiceDate), due: ds(i.dueDate),
        total: n(i.netAmount), paid: n(i.netAmount) - n(i.balanceDue), status: docStatus(s(i.status), "Approved"),
      }) as Row);
    },
  },
  payments: {
    needs: ["customers", "vendors"],
    run: async (d) => {
      const [rc, vp] = await Promise.all([GET("/api/receivables/receipts", PAGE), GET("/api/payables/payments", PAGE)]);
      const cn = new Map(rows(d, "customers").map((c) => [n(c._id), s(c.name)]));
      const vn = new Map(rows(d, "vendors").map((c) => [n(c._id), s(c.name)]));
      const a = items(rc).map((r) => ({ id: `RCT-${pad(n(r.receiptId))}`, _id: n(r.receiptId), date: ds(r.receiptDate), type: "Receipt", party: cn.get(n(r.customerId)) ?? "", ref: s(r.reference), method: s(r.paymentMethod), bank: s(r.bankAccountId), amount: n(r.amount), status: "Posted" }) as Row);
      const b = items(vp).map((r) => ({ id: `PAY-${pad(n(r.paymentId))}`, _id: n(r.paymentId), date: ds(r.paymentDate), type: "Payment", party: vn.get(n(r.vendorId)) ?? "", ref: s(r.reference), method: s(r.paymentMethod), bank: s(r.bankAccountId), amount: n(r.amount), status: "Posted" }) as Row);
      return [...a, ...b].sort((x, y) => s(y.date).localeCompare(s(x.date)));
    },
  },
  expenses: {
    needs: ["accounts"],
    run: async (d) => {
      const code = new Map(rows(d, "accounts").map((a) => [n(a._id), a.id]));
      return items(await GET("/api/dashboard/expenses", PAGE)).map((e) => ({
        id: s(e.expenseNumber), _id: n(e.expenseId), date: ds(e.expenseDate), payee: s(e.payee), account: code.get(n(e.accountId)) ?? s(e.accountId), bank: s(e.bankAccountId), amount: n(e.amount), memo: s(e.memo), status: s(e.status),
      }) as Row);
    },
  },
  journal: {
    needs: [],
    run: async () =>
      items(await GET("/api/ledger/journals", PAGE)).map((j) => ({
        id: s(j.referenceNumber), _id: n(j.entryId), date: ds(j.entryDate), memo: s(j.description), source: s(j.sourceModule) || "Manual", amount: n(j.totalDebit), status: s(j.status),
      }) as Row),
  },
  assetCats: {
    needs: [],
    run: async () => items(await GET("/api/assets/categories", PAGE)).map((c) => ({ id: s(c.categoryId), _id: n(c.categoryId), name: s(c.categoryName), months: n(c.usefulLifeMonths) }) as Row),
  },
  assets: {
    needs: ["assetCats"],
    run: async (d) => {
      const cat = new Map(rows(d, "assetCats").map((c) => [n(c._id), c]));
      return items(await GET("/api/assets/register", PAGE)).map((a) => {
        const c = cat.get(n(a.categoryId));
        const months = n(c?.months) || 12;
        return {
          id: s(a.assetCode), _id: n(a.assetId), name: s(a.assetName), category: s(c?.name), catId: n(a.categoryId), date: ds(a.acquisitionDate), cost: n(a.acquisitionCost), life: Math.round((months / 12) * 10) / 10,
          monthly: Math.max(0, n(a.acquisitionCost) - n(a.residualValue)) / months, accum: n(a.accumulatedDepreciation), nbv: n(a.netBookValue), status: s(a.status),
        } as Row;
      });
    },
  },
  budgets: {
    needs: [],
    run: async () =>
      ((await GET("/api/dashboard/budget-tracking", { period: "year" }))?.lines ?? []).map((l: any) => ({
        id: s(l.accountCode), account: s(l.accountCode), amount: n(l.budget), actual: n(l.actual), remaining: n(l.remaining), used: n(l.usedPercent), state: s(l.status),
      }) as Row),
  },
  ledger: {
    needs: [],
    run: async () =>
      items((await GET("/api/dashboard/ledger/postings", { page: 1, pageSize: 500 }))).map((p) => ({
        id: s(p.lineId), date: ds(p.date), ref: s(p.reference), account: `${s(p.accountCode)} · ${s(p.accountName)}`, memo: s(p.description), dr: n(p.debit), cr: n(p.credit),
      }) as Row),
  },
  trial: {
    needs: [],
    run: async () => ((await GET("/api/dashboard/ledger/trial-balance"))?.rows ?? []).map((r: any) => ({ id: s(r.code), name: s(r.name), type: s(r.type), debit: n(r.debit), credit: n(r.credit) }) as Row),
  },
  audit: {
    needs: [],
    run: async () =>
      items(await GET("/api/compliance/audit-trail", { page: 1, pageSize: 200 })).map((a) => ({
        id: `AU-${pad(n(a.logId), 6)}`, when: new Date(s(a.changedAt)).toLocaleString("en-GB"), user: a.changedBy ? `User ${s(a.changedBy)}` : "System", entity: s(a.tableName), record: s(a.recordId), action: s(a.action),
        detail: s(a.newValues || a.oldValues).slice(0, 140),
      }) as Row),
  },
};

const LABEL: Record<string, string> = {
  accounts: "Chart of accounts", groups: "Account groups", customers: "Customers", vendors: "Vendors", taxes: "Tax rates", banks: "Bank accounts", invoices: "Invoices", payables: "Bills",
  payments: "Payments", expenses: "Expenses", journal: "Journal entries", assetCats: "Asset categories", assets: "Fixed assets", budgets: "Budget", ledger: "General ledger", trial: "Trial balance", audit: "Audit log",
};
export const ALL_KEYS = Object.keys(L);

/** Loads the named collections (and the ones they depend on) in parallel waves. A failing collection is reported, never fatal. */
export async function load(keys: string[], base: Data = {}): Promise<LoadResult> {
  const data: Data = { ...base };
  const errors: string[] = [];
  const want = new Set<string>();
  const add = (k: string) => {
    if (want.has(k) || !L[k]) return;
    want.add(k);
    L[k].needs.forEach(add);
  };
  keys.forEach(add);
  const done = new Set<string>();
  const failed = new Set<string>();
  while (done.size + failed.size < want.size) {
    const wave = [...want].filter((k) => !done.has(k) && !failed.has(k) && L[k].needs.every((x) => done.has(x) || failed.has(x)));
    if (wave.length === 0) break;
    await Promise.all(
      wave.map(async (k) => {
        try {
          data[k] = await L[k].run(data);
          done.add(k);
        } catch (e) {
          failed.add(k);
          data[k] = data[k] ?? [];
          errors.push(`${LABEL[k] ?? k}: ${(e as Error).message}`);
        }
      }),
    );
  }
  return { data, errors };
}

/* =====================================================================================================
   Commands (every form and button ends up here)
   ===================================================================================================== */
const lineOf = (v: Record<string, Val>) => ({ quantity: 1, unitPrice: n(v.net), taxCodeId: v.taxRate ? n(v.taxRate) : null });

export const invoiceEdit = async (r: Row, d: Data): Promise<Record<string, Val>> => {
  const det = await GET(`/api/receivables/invoices/${r._id}`);
  const inv = det?.invoice ?? {};
  const lines: any[] = inv.lines ?? [];
  if (lines.length > 1) throw new ApiError("This invoice has several lines and can't be edited on this screen.", 0);
  const l = lines[0] ?? {};
  const acct = rows(d, "accounts").find((a) => n(a._id) === n(l.revenueAccountId));
  return { customer: String(r.customer), date: String(r.date), due: String(r.due), account: acct?.id ?? "", net: n(l.quantity || 1) * n(l.unitPrice) * (1 - n(l.discountPercent) / 100), taxRate: l.taxCodeId ? String(l.taxCodeId) : "", description: s(l.description) };
};
export const billEdit = async (r: Row, d: Data): Promise<Record<string, Val>> => {
  const det = await GET(`/api/payables/invoices/${r._id}`);
  const inv = det?.invoice ?? {};
  const lines: any[] = inv.lines ?? [];
  if (lines.length > 1) throw new ApiError("This bill has several lines and can't be edited on this screen.", 0);
  const l = lines[0] ?? {};
  const acct = rows(d, "accounts").find((a) => n(a._id) === n(l.expenseAccountId));
  return { vendor: String(r.vendor), number: String(r.id), date: String(r.date), due: String(r.due), account: acct?.id ?? "", net: n(l.quantity || 1) * n(l.unitPrice), taxRate: l.taxCodeId ? String(l.taxCodeId) : "", description: s(l.description) };
};
export const journalEdit = async (r: Row, d: Data): Promise<Record<string, Val>> => {
  const j = await GET(`/api/ledger/journals/${r._id}`);
  const lines: any[] = j?.lines ?? [];
  const dr = lines.find((x) => n(x.debit) > 0);
  const cr = lines.find((x) => n(x.credit) > 0);
  if (lines.length !== 2 || !dr || !cr) throw new ApiError("This entry has more than two lines and can't be edited on this screen.", 0);
  const code = (id: number) => rows(d, "accounts").find((a) => n(a._id) === id)?.id ?? "";
  return { date: ds(j.entryDate), memo: s(j.description), debit: code(n(dr.accountId)), credit: code(n(cr.accountId)), amount: n(dr.debit) };
};

export const invoiceDetail = async (id: number) => {
  const det = await GET(`/api/receivables/invoices/${id}`);
  const receipts = await GET("/api/receivables/receipts", PAGE).then(items).catch(() => [] as any[]);
  const rc = new Map(receipts.map((r: any) => [n(r.receiptId), r]));
  return ((det?.receipts ?? []) as any[]).map((a) => {
    const r = rc.get(n(a.receiptId));
    return { date: ds(r?.receiptDate), method: s(r?.paymentMethod), amount: n(a.amountApplied) };
  });
};
export const billDetail = async (id: number) => {
  const det = await GET(`/api/payables/invoices/${id}`);
  const pays = await GET("/api/payables/payments", PAGE).then(items).catch(() => [] as any[]);
  const pm = new Map(pays.map((p: any) => [n(p.paymentId), p]));
  return ((det?.settlements ?? []) as any[]).map((a) => {
    const p = pm.get(n(a.paymentId));
    return { date: ds(p?.paymentDate), method: s(p?.paymentMethod), amount: n(a.cashBase) + n(a.withholdingAmount) + n(a.discountAmount) };
  });
};
export const journalLines = async (id: number): Promise<{ account: string; debit: number; credit: number; memo: string }[]> => {
  const j = await GET(`/api/ledger/journals/${id}`);
  return ((j?.lines ?? []) as any[]).map((l) => ({ account: `${s(l.accountCode)} · ${s(l.accountName)}`, debit: n(l.debit), credit: n(l.credit), memo: s(l.description) }));
};

const nextCode = (prefix: string, list: Row[]) => `${prefix}${pad(list.reduce((m, r) => Math.max(m, Number(r.id.slice(prefix.length)) || 0), 0) + 1)}`;
const controlAccount = (d: Data, key: "customers" | "vendors", code: string): number => {
  const first = Object.values(raw[key] ?? {})[0];
  return n(first?.controlAccountId) || n(rows(d, "accounts").find((a) => a.id === code)?._id);
};

export const cmd = {
  /* ---- customers & vendors ---- */
  customerSave: async (v: Record<string, Val>, d: Data, ex: Row | null): Promise<string> => {
    const old = ex ? kept("customers", ex.id) : {};
    const body = {
      customerCode: ex ? ex.id : nextCode("CU-", rows(d, "customers")), customerName: s(v.name), controlAccountId: n(old.controlAccountId) || controlAccount(d, "customers", "1200"), paymentTerms: s(v.terms) || null,
      currencyCode: old.currencyCode ?? null, taxId: old.taxId ?? null, contactName: old.contactName ?? null, email: s(v.email) || null, phone: s(v.phone) || null, address: old.address ?? null, creditLimit: n(old.creditLimit),
    };
    if (ex) await PUT(`/api/receivables/customers/${ex._id}`, body);
    else await POST("/api/receivables/customers", body);
    return `${body.customerCode} ${ex ? "saved" : "created"}`;
  },
  vendorSave: async (v: Record<string, Val>, d: Data, ex: Row | null): Promise<string> => {
    const old = ex ? kept("vendors", ex.id) : {};
    const body = {
      vendorCode: ex ? ex.id : nextCode("VN-", rows(d, "vendors")), vendorName: s(v.name), controlAccountId: n(old.controlAccountId) || controlAccount(d, "vendors", "2000"), paymentTerms: s(v.terms) || null,
      currencyCode: old.currencyCode ?? null, taxId: old.taxId ?? null, contactName: old.contactName ?? null, email: s(v.email) || null, phone: s(v.phone) || null, address: old.address ?? null,
      bankName: old.bankName ?? null, bankAccount: old.bankAccount ?? null, bankSortCode: old.bankSortCode ?? null,
    };
    if (ex) await PUT(`/api/payables/vendors/${ex._id}`, body);
    else await POST("/api/payables/vendors", body);
    return `${body.vendorCode} ${ex ? "saved" : "created"}`;
  },
  customerActive: async (r: Row, on: boolean) => {
    await POST(`/api/receivables/customers/${r._id}/${on ? "activate" : "deactivate"}`);
    return `${s(r.name)} ${on ? "activated" : "deactivated"}`;
  },
  vendorActive: async (r: Row, on: boolean) => {
    await POST(`/api/payables/vendors/${r._id}/${on ? "activate" : "deactivate"}`);
    return `${s(r.name)} ${on ? "activated" : "deactivated"}`;
  },

  /* ---- invoices ---- */
  invoiceSave: async (v: Record<string, Val>, d: Data, ex: Row | null): Promise<string> => {
    const body = {
      customerId: dbId(d, "customers", v.customer), invoiceDate: s(v.date), dueDate: s(v.due), currencyCode: null, exchangeRate: null, notes: null, soId: null, invoiceNumber: null,
      lines: [{ itemId: null, description: s(v.description) || "Services", ...lineOf(v), discountPercent: 0, revenueAccountId: dbId(d, "accounts", v.account), costCentreId: null, projectId: null, soLineId: null, obligationId: null }],
    };
    const res = ex ? await PUT(`/api/receivables/invoices/${ex._id}`, body) : await POST("/api/receivables/invoices", body);
    return `${s(res?.invoice?.invoiceNumber) || (ex ? ex.id : "Invoice")} ${ex ? "saved" : "created"}`;
  },
  invoiceDelete: async (r: Row) => {
    await DEL(`/api/receivables/invoices/${r._id}`);
    return `${r.id} deleted`;
  },
  invoiceSend: async (r: Row) => {
    await POST(`/api/receivables/invoices/${r._id}/post`, { overrideCreditLimit: false });
    return `${r.id} sent and posted to the ledger`;
  },
  invoiceVoid: async (r: Row, v: Record<string, Val>) => {
    await POST(`/api/receivables/invoices/${r._id}/cancel`, { reason: s(v.reason) });
    return `${r.id} voided`;
  },
  receive: async (r: Row, v: Record<string, Val>, d: Data) => {
    await POST("/api/receivables/receipts", {
      customerId: dbId(d, "customers", r.customer), receiptDate: s(v.date), paymentMethod: methodOut(s(v.method)), amount: n(v.amount), reference: r.id, bankAccountId: n(v.bank),
      allocations: [{ invoiceId: n(r._id), amountApplied: n(v.amount) }], autoApply: false,
    });
    return `${n(v.amount).toLocaleString()} received for ${r.id}`;
  },

  /* ---- bills ---- */
  billSave: async (v: Record<string, Val>, d: Data, ex: Row | null): Promise<string> => {
    const body = {
      vendorId: dbId(d, "vendors", v.vendor), invoiceNumber: s(v.number), invoiceDate: s(v.date), dueDate: s(v.due), currencyCode: null, exchangeRate: null, notes: null, poId: null,
      lines: [{ description: s(v.description) || "Services", ...lineOf(v), expenseAccountId: dbId(d, "accounts", v.account), costCentreId: null, projectId: null, poLineId: null }],
    };
    const res = ex ? await PUT(`/api/payables/invoices/${ex._id}`, body) : await POST("/api/payables/invoices", body);
    return `${s(res?.invoice?.invoiceNumber) || s(v.number)} ${ex ? "saved" : "created"}`;
  },
  billDelete: async (r: Row) => {
    await DEL(`/api/payables/invoices/${r._id}`);
    return `${r.id} deleted`;
  },
  billApprove: async (r: Row) => {
    await POST(`/api/payables/invoices/${r._id}/post`, { overrideMatch: false });
    return `${r.id} approved and posted to the ledger`;
  },
  billVoid: async (r: Row, v: Record<string, Val>) => {
    await POST(`/api/payables/invoices/${r._id}/cancel`, { reason: s(v.reason) });
    return `${r.id} voided`;
  },
  pay: async (r: Row, v: Record<string, Val>, d: Data) => {
    await POST("/api/payables/payments", {
      vendorId: dbId(d, "vendors", r.vendor), paymentDate: s(v.date), paymentMethod: methodOut(s(v.method)), amount: n(v.amount), reference: r.id, bankAccountId: n(v.bank),
      allocations: [{ invoiceId: n(r._id), amountApplied: n(v.amount), withholdingTaxCodeId: null, takeEarlyDiscount: false }], autoApply: false,
    });
    return `${n(v.amount).toLocaleString()} paid on ${r.id}`;
  },

  /* ---- direct expenses ---- */
  expenseSave: async (v: Record<string, Val>, d: Data, ex: Row | null) => {
    const body = { expenseDate: s(v.date), payee: s(v.payee), accountId: dbId(d, "accounts", v.account), bankAccountId: n(v.bank), amount: n(v.amount), memo: s(v.memo) || null, costCentreId: null, projectId: null };
    const res = ex ? await PUT(`/api/dashboard/expenses/${ex._id}`, body) : await POST("/api/dashboard/expenses", body);
    return `${s(res?.expenseNumber) || (ex ? ex.id : "Expense")} ${ex ? "saved" : "created"}`;
  },
  expenseDelete: async (r: Row) => {
    await DEL(`/api/dashboard/expenses/${r._id}`);
    return `${r.id} deleted`;
  },
  expensePost: async (r: Row) => {
    await POST(`/api/dashboard/expenses/${r._id}/post`);
    return `${r.id} posted`;
  },
  expenseVoid: async (r: Row) => {
    await POST(`/api/dashboard/expenses/${r._id}/void`, { reversalDate: null });
    return `${r.id} voided`;
  },

  /* ---- manual journals ---- */
  journalSave: async (v: Record<string, Val>, d: Data, ex: Row | null) => {
    const body = {
      entryDate: s(v.date), entryTypeId: 1, description: s(v.memo), currencyCode: null, exchangeRate: null, referenceNumber: null, sourceModule: null, sourceReference: null,
      lines: [
        { accountId: dbId(d, "accounts", v.debit), debit: n(v.amount), credit: 0, description: s(v.memo), costCentreId: null, projectId: null, taxCodeId: null, taxAmount: 0, currencyCode: null, exchangeRate: null, fcDebit: 0, fcCredit: 0, dimensions: null },
        { accountId: dbId(d, "accounts", v.credit), debit: 0, credit: n(v.amount), description: s(v.memo), costCentreId: null, projectId: null, taxCodeId: null, taxAmount: 0, currencyCode: null, exchangeRate: null, fcDebit: 0, fcCredit: 0, dimensions: null },
      ],
    };
    const res = ex ? await PUT(`/api/ledger/journals/${ex._id}`, body) : await POST("/api/ledger/journals", body);
    return `${s(res?.referenceNumber) || (ex ? ex.id : "Entry")} ${ex ? "saved" : "created"}`;
  },
  journalDelete: async (r: Row) => {
    await DEL(`/api/ledger/journals/${r._id}`);
    return `${r.id} deleted`;
  },
  journalPost: async (r: Row) => {
    await POST(`/api/ledger/journals/${r._id}/post`);
    return `${r.id} posted to the ledger`;
  },
  journalReverse: async (r: Row, v: Record<string, Val>) => {
    await POST(`/api/ledger/journals/${r._id}/reverse`, { reversalDate: s(v.date), reason: s(v.reason) || null });
    return `${r.id} reversed`;
  },

  /* ---- chart of accounts ---- */
  accountSave: async (v: Record<string, Val>, d: Data, ex: Row | null) => {
    if (ex) {
      await PUT(`/api/ledger/accounts/${ex._id}`, { accountName: s(v.name), groupId: n(v.group) || null, parentAccountId: null, clearParent: false, currencyCode: null, isControl: null, isBank: null, isMonetaryFx: null, allowDirectPosting: null, description: null });
      return `${ex.id} saved`;
    }
    await POST("/api/ledger/accounts", { groupId: n(v.group), accountCode: s(v.code), accountName: s(v.name), parentAccountId: null, currencyCode: null, isControl: false, isBank: false, isMonetaryFx: false, allowDirectPosting: true, description: null });
    return `${s(v.code)} created`;
  },
  accountActive: async (r: Row, on: boolean) => {
    await POST(`/api/ledger/accounts/${r._id}/${on ? "activate" : "deactivate"}`);
    return `${s(r.name)} ${on ? "activated" : "deactivated"}`;
  },

  /* ---- bank accounts ---- */
  bankSave: async (v: Record<string, Val>, d: Data, ex: Row | null) => {
    const body = { ledgerAccountId: ex ? null : dbId(d, "accounts", v.gl), accountName: s(v.name), bankName: s(v.bank), accountNumber: s(v.number) || null };
    if (ex) await PUT(`/api/dashboard/bank-accounts/${ex._id}`, body);
    else await POST("/api/dashboard/bank-accounts", body);
    return `${s(v.name)} ${ex ? "saved" : "created"}`;
  },
  bankDelete: async (r: Row) => {
    await DEL(`/api/dashboard/bank-accounts/${r._id}`);
    return `${s(r.name)} deleted`;
  },

  /* ---- tax rates ---- */
  taxSave: async (v: Record<string, Val>, d: Data, ex: Row | null) => {
    if (ex) {
      await PUT(`/api/tax/codes/${ex._id}`, { ...kept("taxes", ex.id), description: s(v.name), taxType: s(v.type), rate: n(v.rate) });
      return `${ex.id} saved`;
    }
    const code = s(v.code).trim().toUpperCase();
    await POST("/api/tax/codes", { taxCodeId: 0, code, description: s(v.name), taxType: s(v.type), rate: n(v.rate), taxAccountId: dbId(d, "accounts", v.account), isInclusive: false, effectiveFrom: today(), effectiveTo: null, isActive: true });
    return `${code} created`;
  },
  taxActive: async (r: Row, on: boolean) => {
    await PUT(`/api/tax/codes/${r._id}`, { ...kept("taxes", r.id), isActive: on });
    return `${s(r.name)} ${on ? "activated" : "deactivated"}`;
  },

  /* ---- fixed assets ---- */
  assetSave: async (v: Record<string, Val>, d: Data, ex: Row | null) => {
    if (ex) {
      await PUT(`/api/assets/register/${ex._id}`, { assetName: s(v.name), location: null, serialNumber: null, costCentreId: null, projectId: null, tagNumber: null, supplier: null, warrantyUntil: null });
      return `${ex.id} saved`;
    }
    const bank = rows(d, "banks").find((b) => b.id === s(v.bank));
    const res = await POST("/api/assets/register", {
      categoryId: n(v.category), assetCode: null, assetName: s(v.name), acquisitionDate: s(v.date), acquisitionCost: n(v.cost), residualValue: null, location: null, serialNumber: null, costCentreId: null, projectId: null,
      parentAssetId: null, tagNumber: null, inServiceDate: null, methodOverride: null, usefulLifeMonthsOverride: n(v.life) > 0 ? Math.round(n(v.life) * 12) : null, unitsTotal: null, supplier: null,
      offsetAccountCode: bank ? s(bank.gl) : null, postAcquisition: !!bank,
    });
    return `${s(res?.assetCode) || "Asset"} created`;
  },

  /* ---- bank reconciliation ---- */
  recon: {
    get: (bank: string, asOf: string, statement: string) => GET(`/api/dashboard/reconciliation/${bank}`, { asOf, statementBalance: statement === "" ? undefined : n(statement) }),
    toggle: (bank: string, line: number, cleared: boolean, asOf: string, statement: string) => PUT(`/api/dashboard/reconciliation/${bank}/lines/${line}`, { cleared, asOf, statementBalance: statement === "" ? null : n(statement) }),
    all: (bank: string, asOf: string, statement: string) => POST(`/api/dashboard/reconciliation/${bank}/select-all`, { asOf, statementBalance: statement === "" ? null : n(statement) }),
    none: (bank: string, asOf: string, statement: string) => POST(`/api/dashboard/reconciliation/${bank}/select-none`, { asOf, statementBalance: statement === "" ? null : n(statement) }),
    finish: (bank: string, asOf: string, statement: string) => POST(`/api/dashboard/reconciliation/${bank}/finish`, { asOf, statementBalance: n(statement), notes: null }),
  },
};

/* ---------- views fetched by the pages themselves ---------- */
export const view = {
  overview: (year: number, month: number) => GET("/api/dashboard/overview", { year, month }),
  cashflow: () => GET("/api/dashboard/cashflow"),
  profitLoss: (period: string) => GET("/api/dashboard/reports/profit-loss", { period }),
  balanceSheet: (asAt: string) => GET("/api/dashboard/reports/balance-sheet", { asAt }),
  tax: (period: string) => GET("/api/dashboard/reports/tax", { period }),
  budget: (period: string) => GET("/api/dashboard/budget-tracking", { period }),
};

/* =====================================================================================================
   More modules: API areas that have no dedicated screen yet. Each is a live, read-only grid.
   ===================================================================================================== */
export interface ModuleDef {
  id: string;
  group: string;
  title: string;
  subtitle: string;
  path: string;
  query?: Q;
}
export const MODULES: ModuleDef[] = [
  { id: "m-creditnotes", group: "Sales", title: "Credit notes", subtitle: "Credits issued to customers.", path: "/api/receivables/credit-notes" },
  { id: "m-salesorders", group: "Sales", title: "Sales orders", subtitle: "Customer orders waiting to be invoiced.", path: "/api/receivables/sales-orders" },
  { id: "m-contracts", group: "Sales", title: "Revenue contracts", subtitle: "Contracts and performance obligations.", path: "/api/receivables/contracts" },
  { id: "m-purchaseorders", group: "Purchasing", title: "Purchase orders", subtitle: "Orders placed with vendors.", path: "/api/payables/purchase-orders" },
  { id: "m-debitnotes", group: "Purchasing", title: "Debit notes", subtitle: "Credits received from vendors.", path: "/api/payables/debit-notes" },
  { id: "m-paymentruns", group: "Purchasing", title: "Payment runs", subtitle: "Batches of vendor payments.", path: "/api/payables/payment-runs" },
  { id: "m-virtualcards", group: "Purchasing", title: "Virtual cards", subtitle: "Single-use cards issued to pay vendors.", path: "/api/payables/virtual-cards" },
  { id: "m-claims", group: "Spend", title: "Expense claims", subtitle: "Claims submitted by staff.", path: "/api/expenses/claims" },
  { id: "m-advances", group: "Spend", title: "Cash advances", subtitle: "Advances paid to staff.", path: "/api/expenses/advances" },
  { id: "m-travel", group: "Spend", title: "Travel requests", subtitle: "Trips awaiting or past approval.", path: "/api/expenses/travel-requests" },
  { id: "m-cards", group: "Spend", title: "Card transactions", subtitle: "Company card spending.", path: "/api/expenses/card-transactions" },
  { id: "m-cwip", group: "Assets", title: "Assets under construction", subtitle: "Capital work in progress.", path: "/api/assets/cwip" },
  { id: "m-leases", group: "Assets", title: "Leases", subtitle: "Lease contracts and schedules.", path: "/api/assets/leases" },
  { id: "m-assetcats", group: "Assets", title: "Asset categories", subtitle: "Depreciation method and life per category.", path: "/api/assets/categories" },
  { id: "m-scenarios", group: "Planning", title: "Scenarios", subtitle: "What-if planning scenarios.", path: "/api/planning/scenarios" },
  { id: "m-capex", group: "Planning", title: "Capital projects", subtitle: "Capex requests and approvals.", path: "/api/planning/capex" },
  { id: "m-boardpacks", group: "Planning", title: "Board packs", subtitle: "Reports prepared for the board.", path: "/api/planning/board-packs" },
  { id: "m-budgets", group: "Planning", title: "Budget versions", subtitle: "Budgets by fiscal year and status.", path: "/api/planning/budgets" },
  { id: "m-returns", group: "Tax", title: "Tax returns", subtitle: "Returns prepared and filed.", path: "/api/tax/returns" },
  { id: "m-einvoice", group: "Tax", title: "E-invoices", subtitle: "Invoices sent to the tax authority.", path: "/api/tax/einvoice" },
  { id: "m-taxrecon", group: "Tax", title: "Tax reconciliations", subtitle: "Ledger against filed returns.", path: "/api/tax/reconciliations" },
  { id: "m-hedges", group: "Treasury", title: "Hedges", subtitle: "Hedging instruments.", path: "/api/treasury/hedges" },
  { id: "m-investments", group: "Treasury", title: "Investments", subtitle: "Money placed in investments.", path: "/api/treasury/investments" },
  { id: "m-guarantees", group: "Treasury", title: "Guarantees", subtitle: "Bank guarantees issued and received.", path: "/api/treasury/guarantees" },
  { id: "m-deals", group: "Treasury", title: "Treasury deals", subtitle: "Deals booked by the treasury desk.", path: "/api/treasury/deals" },
  { id: "m-feecharges", group: "Treasury", title: "Bank fee charges", subtitle: "Fees charged by banks.", path: "/api/treasury/fee-charges" },
  { id: "m-netting", group: "Treasury", title: "Netting", subtitle: "Intercompany netting runs.", path: "/api/treasury/netting" },
  { id: "m-books", group: "Ledger setup", title: "Ledger books", subtitle: "Primary and parallel ledgers.", path: "/api/ledger/books" },
  { id: "m-recurring", group: "Ledger setup", title: "Recurring journals", subtitle: "Templates that post on a schedule.", path: "/api/ledger/recurring" },
  { id: "m-entities", group: "Master data", title: "Legal entities", subtitle: "Companies in the group.", path: "/api/masterdata/entities" },
  { id: "m-changes", group: "Master data", title: "Master data changes", subtitle: "Requested changes and their approval.", path: "/api/masterdata/changes" },
  { id: "m-duplicates", group: "Master data", title: "Possible duplicates", subtitle: "Records that look the same.", path: "/api/masterdata/duplicates" },
  { id: "m-issues", group: "Compliance", title: "Issues", subtitle: "Control and audit issues.", path: "/api/compliance/issues" },
  { id: "m-pbc", group: "Compliance", title: "Auditor requests", subtitle: "Items requested by auditors.", path: "/api/compliance/pbc" },
  { id: "m-documents", group: "Compliance", title: "Audit documents", subtitle: "Evidence stored for auditors.", path: "/api/compliance/documents" },
];

const HIDE = /^(companyId|rowVersion|createdBy|createdAt|updatedAt|updatedBy)$/i;
const human = (k: string) => k.replace(/([a-z0-9])([A-Z])/g, "$1 $2").replace(/\bId\b/g, "ID").replace(/^./, (c) => c.toUpperCase());

export interface ModuleTable {
  columns: { key: string; label: string; kind: "text" | "money" | "date" | "status" | "num" }[];
  rows: Row[];
}
const moneyKey = /(amount|total|balance|cost|price|value|budget|fee|proceeds|limit|principal|rate)$/i;
const dateKey = /(date|at|until|from|to|due)$/i;
const statusKey = /^(status|state|stage|priority|severity)$/i;

/** Reads one list endpoint and works out sensible columns from the first records. */
export async function loadModule(def: ModuleDef): Promise<ModuleTable> {
  const list = items(await GET(def.path, { ...PAGE, ...(def.query ?? {}) }));
  const first = list.slice(0, 20);
  const keys: string[] = [];
  first.forEach((o) => Object.keys(o ?? {}).forEach((k) => !keys.includes(k) && keys.push(k)));
  const usable = keys.filter((k) => !HIDE.test(k) && first.some((o) => o[k] !== null && o[k] !== undefined && typeof o[k] !== "object"));
  const idKey = usable.find((k) => /Id$/.test(k)) ?? usable[0];
  const ordered = [...usable.filter((k) => /(number|code|name|title|description)$/i.test(k)), ...usable.filter((k) => !/(number|code|name|title|description)$/i.test(k) && k !== idKey)].slice(0, 9);
  const columns = ordered.map((k) => {
    const sample = first.find((o) => o[k] !== null && o[k] !== undefined)?.[k];
    const kind: ModuleTable["columns"][number]["kind"] = statusKey.test(k) ? "status" : typeof sample === "number" && moneyKey.test(k) ? "money" : typeof sample === "string" && /^\d{4}-\d{2}-\d{2}/.test(sample) && dateKey.test(k) ? "date" : typeof sample === "number" ? "num" : "text";
    return { key: k, label: human(k), kind };
  });
  const out: Row[] = list.map((o, i) => {
    const r: Row = { id: s(idKey ? o[idKey] : i + 1) || String(i + 1) };
    columns.forEach((c) => {
      const v = o[c.key];
      r[c.key] = c.kind === "date" ? ds(v) : typeof v === "number" ? v : typeof v === "boolean" ? (v ? "Yes" : "No") : s(v);
    });
    return r;
  });
  return { columns, rows: out };
}